import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold } from 'styles/typography';

import { PayerCandidate } from './eligibilityData';
import PayerCandidateCard from './PayerCandidateCard';

// The payer-disambiguation state: PayerSearch returned multiple candidates for the entered
// payer name, and a person must pick the exact payer before the eligibility check can run.
// Selecting a card + "Run Eligibility Check" completes the PayerSearch action with its payer_id.

interface Props {
  candidates: PayerCandidate[];
  searchTerm?: string | null;
  onRun: (payerId: string) => void;
  isRunning: boolean;
}

export default function SelectPayerView({ candidates, searchTerm, onRun, isRunning }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <Wrapper>
      <ResultsCount>
        {candidates.length} result{candidates.length === 1 ? '' : 's'} found
      </ResultsCount>
      <Explanation>
        Before running an insurance eligibility check, the patient’s payer must be selected from the list of options
        returned{searchTerm ? <> for “{searchTerm}”</> : ''} shown below
      </Explanation>

      <Grid role='radiogroup' aria-label='Payer options'>
        {candidates.map((candidate) => (
          <PayerCandidateCard
            key={candidate.primaryPayerId ?? candidate.stediId ?? candidate.displayName}
            candidate={candidate}
            selected={selectedId === candidate.primaryPayerId}
            onSelect={() => setSelectedId(candidate.primaryPayerId)}
          />
        ))}
      </Grid>

      <Footer>
        <FooterHint>{selectedId ? '' : 'A payer must first be selected to run an eligibility check'}</FooterHint>
        <Button
          disabled={!selectedId || isRunning}
          loading={isRunning}
          onClick={() => selectedId && onRun(selectedId)}
          data-testid='run-eligibility-check'>
          Run Eligibility Check
        </Button>
      </Footer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResultsCount = styled(BodySmallBold)`
  font-size: 15px;
  color: ${colors.black100};
`;

const Explanation = styled(Body)`
  color: ${colors.black75};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid ${colors.black10};
  background: ${colors.white};
`;

const FooterHint = styled(BodySmall)`
  color: ${colors.black50};
`;
